/**
 * siteAddressDisplay — small helpers for the AC3 provenance chip + label
 * override rendering on site surfaces.
 *
 * Address-Coordinate Reconciliation, AC3.
 *
 * `displayAddress(site)`     — what to render in headers / cards.
 *                              `addressLabel` wins when present, falls back
 *                              to `address` otherwise.
 * `provenanceMessage(site)`  — the chip text. Returns null when there's
 *                              nothing worth surfacing (legacy site with no
 *                              provenance stamp; the chip stays hidden).
 *
 * Pure functions; no React imports — kept in `lib/` so non-component
 * surfaces (PDF/DOCX export, print view) can also call them.
 */
import type { SiteEntity } from '../types/site';

/** Pick the address string to render. Prefers a manual UI override
 *  (`addressLabel`) over the canonical `address`. Both empty → empty
 *  string so callers don't have to null-guard. */
export function displayAddress(site: Pick<SiteEntity, 'address' | 'addressLabel'>): string {
  const label = site.addressLabel?.trim();
  if (label) return label;
  return site.address ?? '';
}

/** Returns the message body for the AC3 provenance chip, or null when
 *  the chip should stay hidden (legacy site, no provenance recorded).
 *
 *  Three cases:
 *    - 'manual-override' → label overrides what data calls actually use
 *    - 'reverse-geocode' → address auto-derived from the boundary centroid
 *    - undefined         → render nothing (legacy site, no stamp).
 */
export function provenanceMessage(
  site: Pick<SiteEntity, 'address' | 'addressLabel' | 'geocodingProvenance' | 'addressUpdatedAt'>,
): { text: string; refreshedAt: string | null } | null {
  if (site.geocodingProvenance === 'manual-override') {
    return {
      // The override is UI-only. Surface the canonical address in the chip
      // so the user can still see what's being used for data calls.
      text: site.address
        ? `Display label overrides canonical address: ${site.address}`
        : 'Display label overrides canonical address',
      refreshedAt: site.addressUpdatedAt ?? null,
    };
  }
  if (site.geocodingProvenance === 'reverse-geocode') {
    return {
      text: 'Address derived from boundary centroid',
      refreshedAt: site.addressUpdatedAt ?? null,
    };
  }
  return null;
}

/** Format an ISO timestamp as "MMM d, yyyy" for the chip. Returns empty
 *  string on parse failure so the chip just renders the base message. */
export function formatProvenanceDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
